'use client';

import {
  Select,
  createSelectListCollection,
  IconButton,
  LabelDefault,
  MetaDefault,
  Tooltip,
} from '@terra/ui-design-system';
import { VStack, HStack } from '@terra/ui-styled-system';
import { ArrowDown01Icon, InformationCircleIcon } from '@terra/ui-icons';
import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { useApplicationCredentialOptions } from '../../hooks/useApplicationCredentialOptions';

type ApplicationCredentialSelectProps = {
  value: string;
  onValueChange: (credentialId: string) => void;
  skip?: boolean;
};

export function ApplicationCredentialSelect({
  value,
  onValueChange,
  skip = false,
}: ApplicationCredentialSelectProps) {
  const t = useTranslations('proxy.tracking.configureCredentialsDialog');
  const {
    data: credentials = [],
    isLoading: credentialsLoading,
    isError: credentialsError,
    refetch: refetchCredentials,
  } = useApplicationCredentialOptions({ skip });
  const credentialOptions = useMemo(
    () =>
      createSelectListCollection({
        items: [
          { value: '', label: t('noCredential') },
          ...credentials.map((credential) => ({
            value: credential.id,
            label: `${credential.username} (${credential.role})`,
          })),
        ],
      }),
    [credentials, t],
  );

  return (
    <VStack gap="1" w="full" alignItems="flex-start">
      <Select.Root
        positioning={{ sameWidth: true, strategy: 'fixed' }}
        width="full"
        size="sm"
        value={[value]}
        collection={credentialOptions}
        onValueChange={(event) => onValueChange(event.value[0])}
        disabled={credentialsLoading}
      >
        <Select.Control>
          <Select.Trigger>
            <Select.ValueText
              placeholder={
                credentialsLoading
                  ? t('loadingCredentials')
                  : t('selectPlaceholder')
              }
            />
            <Select.Indicator>
              <ArrowDown01Icon size={16} />
            </Select.Indicator>
          </Select.Trigger>
        </Select.Control>
        <Select.Positioner>
          <Select.Content>
            <Select.ItemGroup>
              {credentialOptions.items.map((option) => (
                <Select.Item key={option.value} item={option}>
                  <Select.ItemText>{option.label}</Select.ItemText>
                  <Select.ItemIndicator />
                </Select.Item>
              ))}
            </Select.ItemGroup>
          </Select.Content>
        </Select.Positioner>
      </Select.Root>
      {credentialsError ? (
        <HStack gap="2" alignItems="center">
          <MetaDefault color="fg.error">{t('loadFailed')}</MetaDefault>
          <MetaDefault
            as="button"
            color="fg.default"
            textDecoration="underline"
            cursor="pointer"
            onClick={() => void refetchCredentials()}
          >
            {t('retry')}
          </MetaDefault>
        </HStack>
      ) : null}
    </VStack>
  );
}

export function ApplicationCredentialSelectLabel() {
  const t = useTranslations('proxy.tracking.configureCredentialsDialog');
  const infoAriaLabel = t('credentialInfoAriaLabel');

  return (
    <HStack gap="1" alignItems="center">
      <LabelDefault fontWeight="semibold" color="fg.muted">
        {t('selectCredential')}
      </LabelDefault>
      <Tooltip title={t('credentialTooltip')}>
        <IconButton size="xs" variant="ghost" aria-label={infoAriaLabel}>
          <InformationCircleIcon size={12} />
        </IconButton>
      </Tooltip>
    </HStack>
  );
}
